import { createRouter, createWebHistory } from "vue-router";
import CalendarView from "@/views/CalendarView.vue";
import Platform365View from "@/views/Operations/Platform365View.vue";
import CalendarCreate from "@/views/CalendarCreate.vue";
import FleetDashboardView from "@/views/FleetDashboardView.vue";
import FuelCardList from "@/views/FuelCards/FuelCardList.vue";
import FuelCardView from "@/views/FuelCards/FuelCardView.vue";
import ContactView from "@/views/Contact/ContactView.vue";
import CustomerCreate from "@/views/Customer/CustomerCreate.vue";
import CustomerEdit from "@/views/Customer/CustomerEdit.vue";
import CustomerList from "@/views/Customer/CustomerList.vue";
import CustomerMerge from "@/views/Customer/CustomerMerge.vue";
import CustomerView from "@/views/Customer/CustomerView.vue";
import CustomerCompanyHealth from "@/views/Customer/CustomerCompanyHealth.vue";
import CustomerMarketingSearch from "@/views/Customer/CustomerMarketingSearch.vue";
import EventRisk from "../views/Events/EventRisk.vue";
import InvoiceCreate from "@/views/Invoice/InvoiceCreate.vue";
import InvoiceAutomationLogView from "@/views/Invoice/InvoiceAutomationLogView.vue";
import StripeReconciliationView from "@/views/Invoice/StripeReconciliationView.vue";
import InvoiceList from "@/views/Invoice/InvoiceList.vue";
import InvoiceView from "@/views/Invoice/InvoiceView.vue";
import PurchaseOrderInvoiceView from "@/views/Invoice/PurchaseOrderInvoiceView.vue";
import JobList from "@/views/Job/JobList.vue";
import JobMap from "@/views/Job/JobMap.vue";
import QuoteList from "@/views/Quote/QuoteList.vue";
import QuoteCreate from "@/views/Quote/QuoteCreate.vue";
import QuoteView from "@/views/Quote/QuoteView.vue";
import QuoteAutomationView from "@/views/Quote/QuoteAutomationView.vue";
import ReferenceCreate from "@/views/Reference/ReferenceCreate.vue";
import ReferenceCustomer from "@/views/Reference/ReferenceCustomer.vue";
import ReferenceImport from "@/views/Reference/ReferenceImport.vue";
import ReferenceView from "@/views/Reference/ReferenceView.vue";
import StaffCreate from "@/views/Staff/StaffCreate.vue";
import StaffDashboardView from "@/views/Staff/StaffDashboardView.vue";
import StaffMap from "@/views/Staff/StaffMap.vue";
import StaffView from "@/views/Staff/StaffView.vue";
import PoliciesView from "@/views/Policies/PoliciesView.vue";
import PolicyEditorView from "@/views/Policies/PolicyEditorView.vue";
import SettingsView from "@/views/SettingsView.vue"
import TicketsCreate from "@/views/Ticket/TicketsCreate.vue";
import TicketsList from "@/views/Ticket/TicketsList.vue";
import TicketsView from "@/views/Ticket/TicketsView.vue";
import VacanciesList from "@/views/Vacancy/VacanciesList.vue";
import VacanciesView from "@/views/Vacancy/VacanciesView.vue";
import TheLoginView from "../views/TheLoginView.vue";
import ResetPasswordView from "@/views/ResetPasswordView.vue";
import VehicleCreate from "@/views/Vehicles/VehicleCreate.vue";
import VehicleView from "@/views/Vehicles/VehicleView.vue";
import PricingManager from "@/views/Pricing/PricingManager.vue";
import BankTransactionsView from "@/views/Finance/BankTransactionsView.vue";
import VatReturnsView from "@/views/Finance/VatReturnsView.vue";
import PayrollRunsView from "@/views/Finance/PayrollRunsView.vue";
import SupplierInvoicesView from "@/views/Finance/SupplierInvoicesView.vue";
import SuperUserDashboard from "@/views/SuperUserDashboard.vue";
import CompaniesHouseQueryLogView from "@/views/CompaniesHouseQueryLogView.vue";
import UnitManagementView from "@/views/UnitManagementView.vue";
import AgentAccessView from "@/views/AgentAccessView.vue";
import UserManagementView from "@/views/UserManagementView.vue";
import UserAuthenticationAuditView from "@/views/UserAuthenticationAuditView.vue";
import ActivityFeedView from "@/views/ActivityFeedView.vue";
import NewsFeedView from "@/views/NewsFeedView.vue";
import TicketNotificationExclusionsView from "@/views/Ticket/TicketNotificationExclusionsView.vue";
import SupplierList from "@/views/Supplier/SupplierList.vue";
import SupplierCreate from "@/views/Supplier/SupplierCreate.vue";
import SupplierView from "@/views/Supplier/SupplierView.vue";
import RequisitionList from "@/views/Requisition/RequisitionList.vue";
import RequisitionView from "@/views/Requisition/RequisitionView.vue";
import ReportsView from "@/views/ReportsView.vue";
import PageViewsAuditView from "@/views/PageViewsAuditView.vue";
import TeamPerformanceView from "@/views/TeamPerformanceView.vue";
import apiClient, { setUnauthorizedHandler } from "@/apiClient";
import { useAuthStore } from "@/stores/AuthStore";
import { getSafeAuthRedirect } from "@/utils/authRedirect";
import {
  CUSTOMER_RECORD_ROLES,
  CUSTOMER_SERVICE_ROLES,
  FLEET_ROLES,
} from "@/utils/navigationAccess";
import dayjs from "dayjs";

const routes = [

  // Landing / Dashboard
  {
    path: "/",
    redirect: { name: "calendar" },
  },
  {
    path: "/dashboard",
    name: "dashboard",
    redirect: { name: "calendar" },
  },
  {
    path: "/dashboard/apps",
    name: "dashboard-apps",
    redirect: { name: "calendar" },
  },
  {
    path: "/dashboard/fleet",
    name: "dashboard-fleet",
    component: FleetDashboardView,
    meta: {
      breadcrumbs: [
        { name: "Fleet Dashboard", link: "/dashboard/fleet" },
      ],
      title: "Fleet Dashboard",
      requiresFleet: true,
      navSection: "fleet",
      navLabel: "Fleet Dashboard",
      navOrder: 10,
      navVisible: true,
    },
  },
  {
    path: "/calendar",
    name: "calendar",
    component: CalendarView,
    meta: {
      breadcrumbs: [{ name: "Calendar", link: "/calendar" }],
      title: "Calendar",
      navSection: "operations",
      navLabel: "Calendar",
      navOrder: 10,
      navVisible: true,
    },
  },
  {
    path: "/operations/365",
    name: "operations-365",
    component: Platform365View,
    meta: {
      breadcrumbs: [{ name: "365", link: "/operations/365" }],
      title: "365",
      requiresManagerOrSuper: true,
      navSection: "operations",
      navLabel: "365",
      navOrder: 35,
      navVisible: true,
    },
  },
  {
    path: "/reports",
    name: "reports",
    component: ReportsView,
    meta: {
      breadcrumbs: [{ name: "Reports", link: "/reports" }],
      title: "Reports",
      requiresFinance: true,
      navSection: "finance",
      navLabel: "Reports",
      navOrder: 70,
      navVisible: true,
    },
  },
  {
    path: "/super/team-performance",
    name: "team-performance",
    component: TeamPerformanceView,
    meta: {
      breadcrumbs: [
        { name: "Super User", link: "/super-dashboard" },
        { name: "Team Performance", link: "/super/team-performance" },
      ],
      title: "Team Performance",
      requiresSuper: true,
      navSection: "admin",
      navLabel: "Team Performance",
      navOrder: 15,
      navVisible: true,
    },
  },
  {
    path: "/super/page-views",
    name: "page-views",
    component: PageViewsAuditView,
    meta: {
      breadcrumbs: [
        { name: "Super User", link: "/super-dashboard" },
        { name: "Page Views", link: "/super/page-views" },
      ],
      title: "Page Views",
      requiresFinanceAudits: true,
      navSection: "admin",
      navLabel: "Page Views",
      navOrder: 50,
      navVisible: true,
    },
  },

  // Login
  {
    path: "/login",
    name: "login",
    component: TheLoginView,
    meta: {
      title: "Login",
    },
  },
  {
    path: "/reset-password",
    name: "reset-password",
    component: ResetPasswordView,
    meta: {
      title: "Reset Password",
      public: true,
    },
  },

  // Calendar
  {
    path: "/calendar/create",
    component: CalendarCreate,
    name: "calendar-create",
    meta: {
      breadcrumbs: [
        { name: "Calendar", link: "/calendar" },
        { name: "Create" },
      ],
      title: "Calendar Entry",
      navSection: "operations",
      navLabel: "Calendar Entry",
      navVisible: false,
      navRecent: false,
    },
  },
  {
    path: "/calendar/day",
    redirect: to => ({
      name: "calendar",
      query: {
        ...to.query,
        view: "day",
      },
    }),
  },

  // Contact
  {
    path: "/contact/:id",
    name: "contact-view",
    component: ContactView,
    meta: {
      breadcrumbs: [{ name: "Contacts", link: "" }, { value: true }],
      title: "Contact",
      requiresCustomerRecords: true,
      navSection: "customers",
      navLabel: "Contact",
      navVisible: false,
    },
  },

  // Customer
  {
    path: "/customer",
    name: "customer-list",
    component: CustomerList,
    meta: {
      breadcrumbs: [{ name: "Customers", link: "/customer" }],
      title: "Customers",
      requiresCustomerRecords: true,
      navSection: "customers",
      navLabel: "Customers",
      navOrder: 10,
      navVisible: true,
    },
  },
  {
    path: "/customer/marketing-search",
    name: "customer-marketing-search",
    component: CustomerMarketingSearch,
    meta: {
      breadcrumbs: [
        { name: "Customers", link: "/customer" },
        { name: "Marketing Search" },
      ],
      title: "Customer Marketing Search",
      requiresCustomerService: true,
      navSection: "customers",
      navLabel: "Marketing Search",
      navOrder: 40,
      navVisible: true,
    },
  },
  {
    path: "/customer/:id/company-health",
    name: "customer-company-health",
    component: CustomerCompanyHealth,
    meta: {
      breadcrumbs: [
        { name: "Customers", link: "/customer" },
        { name: "Company Health" },
      ],
      title: "Company Health",
      requiresFinance: true,
      navSection: "customers",
      navLabel: "Company Health",
      navVisible: false,
    },
  },
  {
    path: "/customer/:id",
    name: "customer-view",
    component: CustomerView,
    meta: {
      breadcrumbs: [{ name: "Customers", link: "/customer?page=1" }, { value: true }],
      title: "Customer",
      requiresCustomerRecords: true,
      navSection: "customers",
      navLabel: "Customer",
      navVisible: false,
    },
  },
  {
    path: "/customer/:id/edit",
    name: "customer-edit",
    component: CustomerEdit,
    meta: {
      title: "Edit Customer",
      parent: null,
      requiresCustomerService: true,
      navSection: "customers",
      navLabel: "Edit Customer",
      navVisible: false,
      navRecent: false,
    },
  },
  {
    path: "/customer/:id/merge",
    name: "customer-merge",
    component: CustomerMerge,
    meta: {
      breadcrumbs: [
        { name: "Customers", link: "/customer" },
        { name: "Merge" },
      ],
      title: "Merge Customer",
      requiresCustomerService: true,
      navSection: "customers",
      navLabel: "Merge Customer",
      navVisible: false,
      navRecent: false,
    },
  },
  {
    path: "/customer/create",
    name: "customer-create",
    component: CustomerCreate,
    meta: {
      breadcrumbs: [
        { name: "Customers", link: "/customer" },
        { name: "Create" },
      ],
      title: "Create Customer",
      requiresCustomerService: true,
      navSection: "customers",
      navLabel: "Create Customer",
      navVisible: false,
      navRecent: false,
    },
  },
  {
    path: "/customer/:id",
    name: "customer-view",
    component: CustomerView,
    meta: {
      breadcrumbs: [{ name: "Customers", link: "/customer" }, { value: true }],
      requiresCustomerRecords: true,
      navSection: "customers",
      navLabel: "Customer",
      navVisible: false,
    },
  },

  {
    path: "/invoice",
    name: "invoice-list",
    component: InvoiceList,
    meta: {
      breadcrumbs: [{ name: "Invoices", link: "/invoice" }],
      title: "Invoices",
      requiresFinance: true,
      navSection: "finance",
      navLabel: "Invoices",
      navOrder: 10,
      navVisible: true,
    },
  },
  {
    path: "/supplier-invoices",
    name: "supplier-invoices",
    component: SupplierInvoicesView,
    meta: {
      breadcrumbs: [{ name: "Supplier Invoices", link: "/supplier-invoices" }],
      title: "Supplier Invoices",
      requiresFinance: true,
      navSection: "finance",
      navLabel: "Supplier Invoices",
      navOrder: 30,
      navVisible: true,
    },
  },
  {
    path: "/suppliers",
    name: "supplier-list",
    component: SupplierList,
    meta: {
      breadcrumbs: [{ name: "Suppliers", link: "/suppliers" }],
      title: "Suppliers",
      requiresSupplierRead: true,
      navSection: "finance",
      navLabel: "Suppliers",
      navOrder: 60,
      navVisible: true,
    },
  },
  {
    path: "/suppliers/create",
    name: "supplier-create",
    component: SupplierCreate,
    meta: {
      breadcrumbs: [
        { name: "Suppliers", link: "/suppliers" },
        { name: "Create" },
      ],
      title: "Create Supplier",
      requiresFinance: true,
      navSection: "finance",
      navLabel: "Create Supplier",
      navVisible: false,
      navRecent: false,
    },
  },
  {
    path: "/suppliers/:id",
    name: "supplier-view",
    component: SupplierView,
    meta: {
      breadcrumbs: [{ name: "Suppliers", link: "/suppliers" }, { value: true }],
      title: "Supplier",
      requiresSupplierRead: true,
      navSection: "finance",
      navLabel: "Supplier",
      navVisible: false,
    },
  },
  {
    path: "/bank-transactions",
    name: "bank-transactions",
    component: BankTransactionsView,
    meta: {
      breadcrumbs: [{ name: "Bank Transactions", link: "/bank-transactions" }],
      title: "Bank Transactions",
      requiresFinanceAudits: true,
      navSection: "finance",
      navLabel: "Bank Transactions",
      navOrder: 40,
      navVisible: true,
    },
  },
  {
    path: "/vat-returns",
    name: "vat-returns",
    component: VatReturnsView,
    meta: {
      breadcrumbs: [{ name: "VAT Returns", link: "/vat-returns" }],
      title: "VAT Returns",
      requiresFinanceAudits: true,
      navSection: "finance",
      navLabel: "VAT Returns",
      navOrder: 50,
      navVisible: true,
    },
  },
  {
    path: "/payroll",
    name: "payroll-runs",
    component: PayrollRunsView,
    meta: {
      breadcrumbs: [{ name: "Payroll", link: "/payroll" }],
      title: "Payroll",
      requiresPayrollFinance: true,
      navSection: "finance",
      navLabel: "Payroll",
      navOrder: 20,
      navVisible: true,
    },
  },
  {
    path: "/payroll/settings",
    name: "payroll-settings",
    component: PayrollRunsView,
    meta: {
      breadcrumbs: [
        { name: "Payroll", link: "/payroll" },
        { name: "Settings", link: "/payroll/settings" },
      ],
      title: "Payroll Settings",
      requiresSuper: true,
      navSection: "finance",
      navLabel: "Payroll Settings",
      navVisible: false,
      navRecent: false,
    },
  },
  {
    path: "/requisitions",
    name: "requisition-list",
    component: RequisitionList,
    meta: {
      breadcrumbs: [{ name: "Requisitions", link: "/requisitions" }],
      title: "Requisitions",
      navSection: "operations",
      navLabel: "Requisitions",
      navOrder: 30,
      navVisible: true,
    },
  },
  {
    path: "/requisitions/:id",
    name: "requisition-view",
    component: RequisitionView,
    meta: {
      breadcrumbs: [{ name: "Requisitions", link: "/requisitions" }, { value: true }],
      title: "Requisition",
      navSection: "operations",
      navLabel: "Requisition",
      navVisible: false,
    },
  },
  {
    path: "/invoice/:id",
    name: "invoice-view",
    component: InvoiceView,
    meta: {
      breadcrumbs: [{ name: "Invoices", link: "/invoice" }, { value: true }],
      title: "View Invoice",
      requiresFinance: true,
      navSection: "finance",
      navLabel: "Invoice",
      navVisible: false,
    },
  },
  {
    path: "/purchase-orders/:id",
    name: "purchase-order-invoice-view",
    component: PurchaseOrderInvoiceView,
    meta: {
      breadcrumbs: [{ name: "Invoices", link: "/invoice" }, { value: true }],
      title: "Customer PO Invoice",
      requiresFinance: true,
      navSection: "finance",
      navLabel: "Customer PO Invoice",
      navVisible: false,
    },
  },
  {
    path: "/invoice/create",
    name: "invoice-create",
    component: InvoiceCreate,
    meta: {
      breadcrumbs: [{ name: "Invoices", link: "/invoice" }, { name: "Create" }],
      title: "Create Invoice",
      requiresFinance: true,
      navSection: "finance",
      navLabel: "Create Invoice",
      navVisible: false,
      navRecent: false,
    },
  },

  {
    path: "/pricing",
    name: "pricing",
    component: PricingManager,
    meta: {
      breadcrumbs: [{ name: "Pricing", link: "/pricing" }],
      title: "Pricing",
      requiresSuper: true,
      navSection: "finance",
      navLabel: "Pricing",
      navOrder: 80,
      navVisible: true,
    },
  },

  // Jobs 
  {
    path: "/job",
    alias: "/jobs",
    name: "Jobs",
    component: JobList,
    meta: {
      title: "Jobs",
      breadcrumbs: [{ name: "Jobs", link: "/job" }],
      navSection: "operations",
      navLabel: "Jobs",
      navOrder: 20,
      navVisible: true,
    },
  },
  {
    path: "/job/map",
    name: "job-map",
    component: JobMap,
    meta: {
      breadcrumbs: [{ name: "Job Map", link: "/job/map" }],
      title: "Job Map",
      requiresCustomerService: true,
      navSection: "customers",
      navLabel: "Job Map",
      navOrder: 35,
      navVisible: true,
    },
  },


  // Staff
  {
    path: "/staff",
    name: "staff-list",
    component: StaffDashboardView,
    meta: {
      breadcrumbs: [{ name: "Staff", link: "/staff" }],
      title: "Staff Dashboard",
      navSection: "people",
      navLabel: "Staff",
      navOrder: 10,
      navVisible: true,
    },
  },
  {
    path: "/staff/map",
    name: "staff-map",
    component: StaffMap,
    meta: {
      breadcrumbs: [{ name: "Staff", link: "/staff" }, { name: "Locations" }],
      title: "Staff Locations",
      navSection: "people",
      navLabel: "Staff Map",
      navOrder: 20,
      navVisible: true,
    },
  },
  {
    path: "/staff/create",
    name: "staff-create",
    component: StaffCreate,
    meta: {
      breadcrumbs: [
        { name: "Staff Create", link: "/staff/create" },
        { value: true },
      ],
      title: "Add Staff",
      navSection: "people",
      navLabel: "Add Staff",
      navVisible: false,
      navRecent: false,
    },
  },
  {
    path: "/staff/:id",
    name: "staff-view",
    component: StaffView,
    meta: {
      breadcrumbs: [{ name: "Staff", link: "/staff" }, { value: true }],
      title: "Staff Information",
      navSection: "people",
      navLabel: "Staff",
      navVisible: false,
    },
  },
  {
    path: "/policies",
    name: "policies",
    component: PoliciesView,
    meta: {
      breadcrumbs: [{ name: "Policies", link: "/policies" }],
      title: "Policies",
      requiresManager: true,
      navSection: "people",
      navLabel: "Policies",
      navOrder: 40,
      navVisible: true,
    },
  },
  {
    path: "/policies/new",
    name: "policy-new",
    component: PolicyEditorView,
    meta: {
      breadcrumbs: [{ name: "Policies", link: "/policies" }, { name: "New" }],
      title: "New Policy",
      requiresManager: true,
      navSection: "people",
      navLabel: "New Policy",
      navVisible: false,
      navRecent: false,
    },
  },
  {
    path: "/policies/:id",
    name: "policy-editor",
    component: PolicyEditorView,
    meta: {
      breadcrumbs: [{ name: "Policies", link: "/policies" }, { value: true }],
      title: "Policy",
      requiresManager: true,
      navSection: "people",
      navLabel: "Policy",
      navVisible: false,
    },
  },


  // Settings
  {
    path: "/settings",
    name: "settings",
    component: SettingsView,
    meta: {
      breadcrumbs: [{ name: "Settings", link: "/settings" }],
      title: "Settings",
      navSection: "admin",
      navLabel: "Settings",
      navOrder: 60,
      navVisible: false,
    },
  },

  // Tickets
  {
    path: "/tickets",
    name: "tickets",
    component: TicketsList,
    meta: {
      breadcrumbs: [{ name: "Tickets", link: "/tickets" }],
      title: "Tickets",
      requiresCustomerRecords: true,
      navSection: "customers",
      navLabel: "Tickets",
      navOrder: 30,
      navVisible: true,
    },
  },
  {
    path: "/tickets/search",
    name: "tickets-search",
    component: () => import("@/views/Ticket/TicketSearchResults.vue"),
    meta: {
      breadcrumbs: [{ name: "Tickets", link: "/tickets" }, { name: "Search results" }],
      title: "Ticket search",
      requiresCustomerRecords: true,
      navVisible: false,
    },
  },
  {
    path: "/tickets/create",
    name: "tickets-create",
    component: TicketsCreate,
    meta: {
      breadcrumbs: [{ name: "Tickets", link: "/tickets" }],
      title: "Create ticket",
      requiresCustomerService: true,
      navSection: "customers",
      navLabel: "Create Ticket",
      navVisible: false,
      navRecent: false,
    },
  },
  {
    path: "/tickets/:id",
    name: "tickets-view",
    component: TicketsView,
    meta: {
      breadcrumbs: [{ name: "Tickets", link: "/tickets" }, { value: true }],
      title: "View Ticket",
      requiresCustomerRecords: true,
      navSection: "customers",
      navLabel: "Ticket",
      navVisible: false,
    },
  },
  {
    path: "/vacancies",
    name: "vacancies-list",
    component: VacanciesList,
    meta: {
      breadcrumbs: [{ name: "Vacancies", link: "/vacancies" }],
      title: "Vacancies",
      navSection: "people",
      navLabel: "Vacancies",
      navOrder: 30,
      navVisible: true,
    },
  },
  {
    path: "/vacancies/:id",
    name: "vacancies-view",
    component: VacanciesView,
    meta: {
      breadcrumbs: [{ name: "Vacancies", link: "/vacancies" }, { value: true }],
      title: "Vacancy Application",
      navSection: "people",
      navLabel: "Vacancy",
      navVisible: false,
    },
  },

  // Quotes
  {
    path: "/quotes",
    name: "quotes",
    component: QuoteList,
    meta: {
      breadcrumbs: [{ name: "Quotes", link: "/quotes" }],
      title: "Quotes",
      requiresCustomerRecords: true,
      navSection: "customers",
      navLabel: "Quotes",
      navOrder: 20,
      navVisible: true,
    },
  },
  {
    path: "/quotes/create",
    name: "quotes-create",
    component: QuoteCreate,
    meta: {
      breadcrumbs: [{ name: "Quotes", link: "/quotes" }, { name: "Create" }],
      title: "Create Quote",
      requiresCustomerService: true,
      navSection: "customers",
      navLabel: "Create Quote",
      navVisible: false,
      navRecent: false,
    },
  },
  {
    path: "/quotes/:id",
    name: "quotes-view",
    component: QuoteView,
    meta: {
      breadcrumbs: [{ name: "Quotes", link: "/quotes" }, { value: true }],
      title: "Quote",
      requiresCustomerRecords: true,
      navSection: "customers",
      navLabel: "Quote",
      navVisible: false,
    },
  },

  // Reference
  {
    path: "/reference/create",
    name: "reference-create",
    component: ReferenceCreate,
    meta: {
      title: "Create Job",
      navSection: "operations",
      navLabel: "Create Job",
      navVisible: false,
      navRecent: false,
    },
  },
  {
    path: "/reference/import",
    name: "reference-import",
    component: ReferenceImport,
    meta: {
      breadcrumbs: [{ name: "Job", link: "/job" }, { name: "Import" }],
      title: "Import Jobs",
      navSection: "operations",
      navLabel: "Import Jobs",
      navVisible: false,
      navRecent: false,
    },
  },
  {
    path: "/reference/:id",
    name: "reference-view",
    component: ReferenceView,
    meta: {
      breadcrumbs: [{ name: "Job", link: "/job" }, { value: true }],
      title: "Job",
      navSection: "operations",
      navLabel: "Job",
      navVisible: false,
    },
  },
  {
    path: "/reference/:id/event-risk",
    name: "event-risk",
    component: EventRisk,
    meta: {
      breadcrumbs: [{ name: "Job", link: "/job" }, { value: true }, { name: "Event Risks" }],
      title: "Event Risks",
      navSection: "operations",
      navLabel: "Event Risk",
      navVisible: false,
    },
  },
  {
    path: "/reference/customer",
    name: "reference-customer",
    component: ReferenceCustomer,
    meta: {
      title: "Customer Reference",
      requiresCustomerRecords: true,
      navSection: "customers",
      navLabel: "Customer Reference",
      navVisible: false,
    },
  },


  // Vehicles
  {
    path: "/vehicles",
    name: "vehicles",
    redirect: { name: "dashboard-fleet" },
    meta: {
      requiresFleet: true,
      navSection: "fleet",
    },
  },
  {
    path: "/fuel-cards",
    name: "fuel-cards",
    component: FuelCardList,
    meta: {
      breadcrumbs: [
        { name: "Fleet Dashboard", link: "/dashboard/fleet" },
        { name: "Fuel Cards", link: "/fuel-cards" },
      ],
      title: "Fuel Cards",
      requiresFleet: true,
      navSection: "fleet",
      navLabel: "Fuel Cards",
      navOrder: 20,
      navVisible: true,
    },
  },
  {
    path: "/fuel-cards/:id",
    name: "fuel-cards-view",
    component: FuelCardView,
    meta: {
      breadcrumbs: [
        { name: "Fleet Dashboard", link: "/dashboard/fleet" },
        { name: "Fuel Cards", link: "/fuel-cards" },
        { value: true },
      ],
      title: "Fuel Card",
      requiresFleet: true,
      navSection: "fleet",
      navLabel: "Fuel Card",
      navVisible: false,
    },
  },
  {
    path: "/vehicles/create",
    name: "vehicles-create",
    component: VehicleCreate,
    meta: {
      breadcrumbs: [
        { name: "Fleet Dashboard", link: "/dashboard/fleet" },
        { name: "Create" },
      ],
      title: "Create Vehicle",
      requiresFleet: true,
      navSection: "fleet",
      navLabel: "Create Vehicle",
      navVisible: false,
      navRecent: false,
    },
  },
  {
    path: "/vehicles/:id",
    name: "vehicles-view",
    component: VehicleView,
    meta: {
      breadcrumbs: [
        { name: "Fleet Dashboard", link: "/dashboard/fleet" },
        { value: true },
      ],
      title: "Vehicle",
      requiresFleet: true,
      navSection: "fleet",
      navLabel: "Vehicle",
      navVisible: false,
    },
  },
  {
    path: "/super-dashboard",
    name: "super-dashboard",
    component: SuperUserDashboard,
    meta: {
      breadcrumbs: [{ name: "Super User", link: "/super-dashboard" }],
      title: "Super User",
      requiresSuper: true,
      navSection: "admin",
      navLabel: "Super Dashboard",
      navOrder: 10,
      navVisible: true,
    },
  },
  {
    path: "/super/companies-house-queries",
    name: "companies-house-query-log",
    component: CompaniesHouseQueryLogView,
    meta: {
      breadcrumbs: [
        { name: "Super User", link: "/super-dashboard" },
        { name: "Companies House Queries" },
      ],
      title: "Companies House Queries",
      requiresSuper: true,
      navSection: "admin",
      navLabel: "Companies House Queries",
      navVisible: false,
    },
  },
  {
    path: "/manager/news",
    name: "news-feed",
    component: NewsFeedView,
    meta: {
      breadcrumbs: [
        { name: "Management", link: "/manager/news" },
        { name: "News Feed", link: "/manager/news" },
      ],
      title: "News Feed",
      requiresManager: true,
      navSection: "customers",
      navLabel: "News Feed",
      navOrder: 15,
      navVisible: false,
      navRecent: false,
    },
  },
  {
    path: "/manager/competitors",
    redirect: to => ({
      name: "news-feed",
      query: { ...to.query, category: "competitor" },
    }),
  },
  {
    path: "/super/agent-access",
    name: "agent-access",
    component: AgentAccessView,
    meta: { title: "Agent access", requiresSuper: true, breadcrumbs: [{ name: "Super User", link: "/super-dashboard" }, { name: "Agent access", link: "/super/agent-access" }] },
  },
  {
    path: "/super/users",
    name: "user-management",
    component: UserManagementView,
    meta: {
      breadcrumbs: [
        { name: "Super User", link: "/super-dashboard" },
        { name: "User Management", link: "/super/users" },
      ],
      title: "User Management",
      requiresSuper: true,
      navSection: "admin",
      navLabel: "Users",
      navOrder: 20,
      navVisible: true,
    },
  },
  {
    path: "/super/users/:id/authentication-audit",
    name: "user-authentication-audit",
    component: UserAuthenticationAuditView,
    meta: {
      breadcrumbs: [
        { name: "Super User", link: "/super-dashboard" },
        { name: "User Management", link: "/super/users" },
        { name: "Authentication Audit" },
      ],
      title: "Authentication Audit",
      requiresSuper: true,
      navVisible: false,
    },
  },
  {
    path: "/super/units",
    name: "unit-management",
    component: UnitManagementView,
    meta: {
      breadcrumbs: [
        { name: "Super User", link: "/super-dashboard" },
        { name: "Unit Management", link: "/super/units" },
      ],
      title: "Unit Management",
      requiresSuper: true,
      navSection: "admin",
      navLabel: "Units",
      navOrder: 25,
      navVisible: true,
    },
  },
  {
    path: "/super/ticket-notifications",
    name: "ticket-notifications",
    component: TicketNotificationExclusionsView,
    meta: {
      breadcrumbs: [
        { name: "Super User", link: "/super-dashboard" },
        { name: "Ticket Notifications", link: "/super/ticket-notifications" },
      ],
      title: "Ticket Notifications",
      requiresSuper: true,
      navSection: "admin",
      navLabel: "Ticket Notifications",
      navOrder: 30,
      navVisible: true,
    },
  },
  {
    path: "/super/invoice-automation",
    name: "invoice-automation",
    component: InvoiceAutomationLogView,
    meta: {
      breadcrumbs: [
        { name: "Super User", link: "/super-dashboard" },
        { name: "Invoice Automation", link: "/super/invoice-automation" },
      ],
      title: "Invoice Automation",
      requiresSuper: true,
      navSection: "admin",
      navLabel: "Invoice Automation",
      navOrder: 35,
      navVisible: true,
    },
  },
  {
    path: "/super/stripe-invoices",
    name: "stripe-reconciliation",
    component: StripeReconciliationView,
    meta: {
      breadcrumbs: [
        { name: "Super User", link: "/super-dashboard" },
        { name: "Stripe Reconciliation", link: "/super/stripe-invoices" },
      ],
      title: "Stripe Reconciliation",
      requiresSuper: true,
      navSection: "admin",
      navLabel: "Stripe Reconciliation",
      navOrder: 36,
      navVisible: true,
    },
  },
  {
    path: "/super/quote-automation",
    name: "quote-automation",
    component: QuoteAutomationView,
    meta: {
      breadcrumbs: [
        { name: "Super User", link: "/super-dashboard" },
        { name: "Quotation Automation", link: "/super/quote-automation" },
      ],
      title: "Quotation Automation",
      requiresSuper: true,
      navSection: "admin",
      navLabel: "Quotation Automation",
      navOrder: 37,
      navVisible: true,
    },
  },
  {
    path: "/super/activity",
    name: "activity-feed",
    component: ActivityFeedView,
    meta: {
      breadcrumbs: [
        { name: "Super User", link: "/super-dashboard" },
        { name: "Activity Feed", link: "/super/activity" },
      ],
      title: "Activity Feed",
      requiresSuper: true,
      navSection: "admin",
      navLabel: "Activity Feed",
      navOrder: 40,
      navVisible: true,
    },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

let redirectingToLogin = false;

setUnauthorizedHandler(() => {
  const auth = useAuthStore();
  const currentRoute = router.currentRoute.value;
  auth.clearSession();

  // During the initial navigation, the route guard below owns the redirect and
  // has access to the page the user originally requested.
  if (
    currentRoute.matched.length === 0
    || currentRoute.name === "login"
    || redirectingToLogin
  ) {
    return;
  }

  redirectingToLogin = true;
  void router.replace({
    name: "login",
    query: { redirect: getSafeAuthRedirect(currentRoute.fullPath) },
  }).finally(() => {
    redirectingToLogin = false;
  });
});

let lastRoute = null;
let lastStart = Date.now();

const trackPageView = async (route, durationMs) => {
  const auth = useAuthStore();
  if (!auth.isLoggedIn || route.meta?.public) {
    return;
  }
  try {
    await apiClient.post("/api/analytics/pageview", {
      path: route.fullPath,
      title: route.meta?.title || document.title,
      referrer: document.referrer || "",
      duration_ms: durationMs,
    });
  } catch {
    // Ignore analytics failures
  }
};

router.afterEach((to) => {
  const now = Date.now();
  if (lastRoute) {
    const duration = Math.max(0, now - lastStart);
    trackPageView(lastRoute, duration);
  }
  lastRoute = to;
  lastStart = now;
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  await auth.initialize();

  if (!to.meta?.public && to.name !== "login" && !auth.isLoggedIn) {
    return {
      name: "login",
      query: { redirect: to.fullPath },
      replace: true,
    };
  }

  if (to.name === "login" && auth.isLoggedIn) {
    return getSafeAuthRedirect(to.query.redirect);
  }

  const hasFinanceRole = auth.hasFinanceRole();
  const hasPayrollFinance = auth.hasAnyRole(["finance-department", "super"]);
  const hasCustomerAccess = auth.hasAnyRole(CUSTOMER_RECORD_ROLES);
  const hasCustomerServiceAccess = auth.hasAnyRole(CUSTOMER_SERVICE_ROLES);
  const hasFleetAccess = auth.hasAnyRole(FLEET_ROLES);

  if (to.path === "/calendar") {
    const rawDate = typeof to.query?.date === "string" ? to.query.date : null;
    const parsedDate = rawDate ? dayjs(rawDate) : null;

    if (!parsedDate || !parsedDate.isValid()) {
      return {
        path: "/calendar",
        query: {
          ...to.query,
          date: dayjs().format("YYYY-MM-DD"),
        },
        replace: true,
      };
    }
  }

  if (to.meta?.requiresCustomerService && !hasCustomerServiceAccess) {
    return { name: "calendar" };
  }
  if (to.meta?.requiresCustomerRecords && !hasCustomerAccess) {
    return { name: "calendar" };
  }
  if (
    to.meta?.requiresPayrollFinance
    && !hasPayrollFinance
  ) {
    return { name: "calendar" };
  }
  if (
    to.meta?.requiresFinance
    && !hasFinanceRole
  ) {
    return { name: "calendar" };
  }
  if (
    to.meta?.requiresSupplierRead
    && !hasFinanceRole
    && !auth.hasRole("operational")
    && !auth.hasRole("super")
  ) {
    return { name: "calendar" };
  }
  if (
    to.meta?.requiresFinanceAudits
    && !hasFinanceRole
  ) {
    return { name: "calendar" };
  }
  if (to.meta?.requiresSuper && !auth.hasRole("super")) {
    return { name: "calendar" };
  }
  if (to.meta?.requiresManager && !auth.hasRole("manager")) {
    return { name: "calendar" };
  }
  if (
    to.meta?.requiresManagerOrSuper
    && !auth.hasAnyRole(["manager", "super"])
  ) {
    return { name: "calendar" };
  }
  if (to.meta?.requiresFleet && !hasFleetAccess) {
    return { name: "calendar" };
  }
  return true;
});

export default router;
